// components/booking/activities/PersonSelector.tsx - Simplified person selector (single "Persons" row)

'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import styles from './PersonSelector.module.css'

interface PersonSelectorProps {
  persons: number
  minPersons?: number
  maxPersons?: number
  pricePerPerson: number
  onUpdate: (persons: number) => void
  showPricing?: boolean
}

export default function PersonSelector({
  persons,
  minPersons = 1,
  maxPersons = 12,
  pricePerPerson,
  onUpdate,
  showPricing = true
}: PersonSelectorProps) {
  const t = useTranslations('bookingActivity.personSelector')
  const [localPersons, setLocalPersons] = useState(persons)

  const subtotal = localPersons * pricePerPerson

  const canAdd = localPersons < maxPersons
  const canRemove = localPersons > minPersons

  useEffect(() => {
    setLocalPersons(persons)
  }, [persons])

  const handleChange = (delta: number) => {
    const newPersons = Math.max(minPersons, Math.min(maxPersons, localPersons + delta))
    if (newPersons !== localPersons) {
      setLocalPersons(newPersons)
      onUpdate(newPersons)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('title')}</h3>
        <p className={styles.subtitle}>{t('subtitle')}</p>
      </div>

      <div className={styles.selectors}>
        {/* Persons */}
        <div className={styles.selectorRow}>
          <div className={styles.info}>
            <div className={styles.label}>
              <PersonIcon />
              <span>{t('persons')}</span>
            </div>
            {showPricing && (
              <div className={styles.price}>${pricePerPerson} {t('perPerson')}</div>
            )}
          </div>
          <div className={styles.controls}>
            <button
              className={styles.button}
              onClick={() => handleChange(-1)}
              disabled={!canRemove}
              aria-label={t('decreasePersons')}
            >
              <MinusIcon />
            </button>
            <span className={styles.count}>{localPersons}</span>
            <button
              className={styles.button}
              onClick={() => handleChange(1)}
              disabled={!canAdd}
              aria-label={t('increasePersons')}
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {showPricing && (
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('totalParticipants')}</span>
            <span className={styles.summaryValue}>{localPersons}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('subtotal')}</span>
            <span className={styles.summaryTotal}>${subtotal.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Icons
function PersonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 10C12.0711 10 13.75 8.32107 13.75 6.25C13.75 4.17893 12.0711 2.5 10 2.5C7.92893 2.5 6.25 4.17893 6.25 6.25C6.25 8.32107 7.92893 10 10 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17.5 17.5C17.5 14.1863 14.1421 11.25 10 11.25C5.85786 11.25 2.5 14.1863 2.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function MinusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
